import { ISchedule, IScheduleEntry } from '@app-types/websocket/schedule.types';
import { createArrayCopyAndSort, createScheduleSide, findFirstAndLastEntry, makeSideToMatch, mergeAndClean } from '../ScheduleCalc';
import { addDays } from '@helpers/DateHelper';

const UNSPECIFIED_LOCATION = "";
type ScheduleSide = "A"|"B";

interface IEntryWithSide {
  entry:IScheduleEntry;
  side:ScheduleSide;
}

const applyChanges = (schedule:ISchedule, shift:number, doFlip:boolean, startDate?:Date):ISchedule => {

  const changedSchedule = applyWholeScheduleChanges(schedule, shift, doFlip);
  if (startDate === undefined || (shift === 0 && !doFlip)) {
    return changedSchedule;
  }

  return mergeScheduleChangeFromDate(schedule, changedSchedule, startDate);
}

const applyWholeScheduleChanges = (schedule:ISchedule, shift:number, doFlip:boolean):ISchedule => {
  const newSchedule:ISchedule = {
    id:schedule.id,
    lastHandover:schedule.lastHandover,
    status:schedule.status,
    name:schedule.name,
    school:schedule.school,
    data:{
      children:schedule.data.children,
      parentA:schedule.data.parentA,
      parentB:schedule.data.parentB,
      scheduleEntriesParentA:{},
      scheduleEntriesParentB:{},
    }
  }

  const baseData = doFlip
    ?createArrayCopyAndSort(schedule.data.scheduleEntriesParentB)
    :createArrayCopyAndSort(schedule.data.scheduleEntriesParentA)

  const modifiedEntries = mergeAndClean(shiftDays(baseData, shift));
  const foundEntries = findFirstAndLastEntry(schedule.data.scheduleEntriesParentB);
  const matchingEntries = makeSideToMatch(modifiedEntries, foundEntries.firstEntry, foundEntries.lastEntry);
  newSchedule.data.scheduleEntriesParentA = createScheduleSide(modifiedEntries);
  newSchedule.data.scheduleEntriesParentB = createScheduleSide(matchingEntries);

  return newSchedule;
}

const mergeScheduleChangeFromDate = (originalSchedule:ISchedule, changedSchedule:ISchedule, startDate:Date):ISchedule => {
  const switchTime = getSwitchTime(startDate);
  const dayStart = getStartOfDay(startDate);
  const dayEnd = getStartOfNextDay(startDate);
  const originalWithoutSwitchDayVisits = removeDayVisitsOnDay(originalSchedule, dayStart, dayEnd);
  const changedWithoutSwitchDayVisits = removeDayVisitsOnDay(changedSchedule, dayStart, dayEnd);

  const originalSides = keepOriginalBeforeSwitchDay(originalWithoutSwitchDayVisits, dayStart, switchTime);
  const changedSides = keepChangedAfterSwitchDay(changedWithoutSwitchDayVisits, switchTime, dayEnd);

  const newSchedule:ISchedule = {
    id:originalSchedule.id,
    lastHandover:originalSchedule.lastHandover,
    status:originalSchedule.status,
    name:originalSchedule.name,
    school:originalSchedule.school,
    data:{
      children:originalSchedule.data.children,
      parentA:originalSchedule.data.parentA,
      parentB:originalSchedule.data.parentB,
      scheduleEntriesParentA:createScheduleSide(mergeWithUniqueChangedIds(originalSides.sideA, changedSides.sideA)),
      scheduleEntriesParentB:createScheduleSide(mergeWithUniqueChangedIds(originalSides.sideB, changedSides.sideB)),
    }
  }

  return newSchedule;
}

const removeDayVisitsOnDay = (schedule:ISchedule, dayStart:number, dayEnd:number):ISchedule => {
  let sideA = createArrayCopyAndSort(schedule.data.scheduleEntriesParentA);
  let sideB = createArrayCopyAndSort(schedule.data.scheduleEntriesParentB);

  sideA = removeDayVisitsFromSide(sideA, sideB, dayStart, dayEnd);
  sideB = removeDayVisitsFromSide(sideB, sideA, dayStart, dayEnd);

  const newSchedule:ISchedule = {
    id:schedule.id,
    lastHandover:schedule.lastHandover,
    status:schedule.status,
    name:schedule.name,
    school:schedule.school,
    data:{
      children:schedule.data.children,
      parentA:schedule.data.parentA,
      parentB:schedule.data.parentB,
      scheduleEntriesParentA:createScheduleSide(mergeAndClean(sideA)),
      scheduleEntriesParentB:createScheduleSide(mergeAndClean(sideB)),
    }
  }

  return newSchedule;
}

const removeDayVisitsFromSide = (visitSide:IScheduleEntry[], ownerSide:IScheduleEntry[], dayStart:number, dayEnd:number) => {
  const dayVisits = visitSide.filter((entry) => isDayVisitOnDay(entry, dayStart, dayEnd));

  for (const dayVisit of dayVisits) {
    healOwnerSideForRemovedVisit(ownerSide, dayVisit);
  }

  return visitSide.filter((entry) => dayVisits.indexOf(entry) === -1);
}

const isDayVisitOnDay = (entry:IScheduleEntry, dayStart:number, dayEnd:number) => {
  return entry.startTime >= dayStart && entry.endTime <= dayEnd;
}

const healOwnerSideForRemovedVisit = (ownerSide:IScheduleEntry[], removedVisit:IScheduleEntry) => {
  const beforeVisit = ownerSide.find((entry) => entry.endTime === removedVisit.startTime);
  const afterVisit = ownerSide.find((entry) => entry.startTime === removedVisit.endTime);

  if (beforeVisit !== undefined && afterVisit !== undefined && beforeVisit !== afterVisit) {
    beforeVisit.endTime = afterVisit.endTime;
    beforeVisit.endAtSchool = afterVisit.endAtSchool;
    beforeVisit.endLoc = afterVisit.endLoc;
    removeEntry(ownerSide, afterVisit);
  } else if (beforeVisit !== undefined) {
    beforeVisit.endTime = removedVisit.endTime;
    beforeVisit.endAtSchool = removedVisit.endAtSchool;
    beforeVisit.endLoc = removedVisit.endLoc;
  } else if (afterVisit !== undefined) {
    afterVisit.startTime = removedVisit.startTime;
    afterVisit.startAtSchool = removedVisit.startAtSchool;
    afterVisit.startLoc = removedVisit.startLoc;
  }
}

const removeEntry = (entries:IScheduleEntry[], entryToRemove:IScheduleEntry) => {
  const removeIndex = entries.indexOf(entryToRemove);
  if (removeIndex !== -1) {
    entries.splice(removeIndex, 1);
  }
}

const keepOriginalBeforeSwitchDay = (schedule:ISchedule, dayStart:number, switchTime:number) => {
  const sideA = createArrayCopyAndSort(schedule.data.scheduleEntriesParentA);
  const sideB = createArrayCopyAndSort(schedule.data.scheduleEntriesParentB);
  const result = createEmptySides();
  const lastOriginalEntry = findLastOriginalEntry(sideA, sideB, dayStart, switchTime);

  if (lastOriginalEntry === undefined) {
    return result;
  }

  result.sideA = keepEntriesEndingBefore(sideA, lastOriginalEntry.entry.startTime);
  result.sideB = keepEntriesEndingBefore(sideB, lastOriginalEntry.entry.startTime);

  const lastEntry = Object.assign({}, lastOriginalEntry.entry);
  if (lastEntry.startTime >= dayStart) {
    lastEntry.startTime = dayStart;
    setStartHandoverUnspecified(lastEntry);
  }
  lastEntry.endTime = switchTime;
  setEndHandoverUnspecified(lastEntry);
  addEntryToSide(result, lastOriginalEntry.side, lastEntry);

  result.sideA = mergeAndClean(result.sideA);
  result.sideB = mergeAndClean(result.sideB);
  return result;
}

const keepChangedAfterSwitchDay = (schedule:ISchedule, switchTime:number, dayEnd:number) => {
  const sideA = createArrayCopyAndSort(schedule.data.scheduleEntriesParentA);
  const sideB = createArrayCopyAndSort(schedule.data.scheduleEntriesParentB);
  const result = createEmptySides();
  const firstChangedEntry = findFirstChangedEntry(sideA, sideB, switchTime, dayEnd);

  if (firstChangedEntry === undefined) {
    return result;
  }

  const firstEntry = Object.assign({}, firstChangedEntry.entry);
  firstEntry.startTime = switchTime;
  setStartHandoverUnspecified(firstEntry);
  addEntryToSide(result, firstChangedEntry.side, firstEntry);

  result.sideA = result.sideA.concat(keepEntriesStartingAfter(sideA, firstChangedEntry.entry.endTime));
  result.sideB = result.sideB.concat(keepEntriesStartingAfter(sideB, firstChangedEntry.entry.endTime));

  result.sideA = mergeAndClean(result.sideA);
  result.sideB = mergeAndClean(result.sideB);
  return result;
}

const createEmptySides = ():{sideA:IScheduleEntry[], sideB:IScheduleEntry[]} => {
  return {sideA:[], sideB:[]};
}

const addEntryToSide = (sides:{sideA:IScheduleEntry[], sideB:IScheduleEntry[]}, side:ScheduleSide, entry:IScheduleEntry) => {
  if (entry.startTime >= entry.endTime) {
    return;
  }

  if (side === "A") {
    sides.sideA.push(entry);
  } else {
    sides.sideB.push(entry);
  }
}

const keepEntriesEndingBefore = (entries:IScheduleEntry[], time:number) => {
  return entries.filter((entry) => entry.endTime <= time).map((entry) => Object.assign({}, entry));
}

const keepEntriesStartingAfter = (entries:IScheduleEntry[], time:number) => {
  return entries.filter((entry) => entry.startTime >= time).map((entry) => Object.assign({}, entry));
}

const findLastOriginalEntry = (sideA:IScheduleEntry[], sideB:IScheduleEntry[], dayStart:number, switchTime:number)
  :IEntryWithSide|undefined => {
  const entries = createEntriesWithSides(sideA, sideB);
  const entryAtDayStart = findEntryAtTime(entries, dayStart);
  if (entryAtDayStart !== undefined) {
    return entryAtDayStart;
  }

  const morningEntries = entries.filter(({entry}) => entry.startTime < switchTime && entry.endTime > dayStart);
  if (morningEntries.length > 0) {
    return morningEntries.sort((a,b) => a.entry.startTime - b.entry.startTime)[0];
  }

  const entriesBeforeSwitch = entries.filter(({entry}) => entry.startTime < switchTime);
  if (entriesBeforeSwitch.length > 0) {
    return entriesBeforeSwitch.sort((a,b) => b.entry.endTime - a.entry.endTime)[0];
  }

  return undefined;
}

const findFirstChangedEntry = (sideA:IScheduleEntry[], sideB:IScheduleEntry[], switchTime:number, dayEnd:number)
  :IEntryWithSide|undefined => {
  const entries = createEntriesWithSides(sideA, sideB);
  const entryAtDayEnd = findEntryAtTime(entries, dayEnd - 1);
  if (entryAtDayEnd !== undefined) {
    return entryAtDayEnd;
  }

  const afternoonEntries = entries.filter(({entry}) => entry.startTime < dayEnd && entry.endTime > switchTime);
  if (afternoonEntries.length > 0) {
    return afternoonEntries.sort((a,b) => b.entry.endTime - a.entry.endTime)[0];
  }

  const entriesAfterSwitch = entries.filter(({entry}) => entry.endTime > switchTime);
  if (entriesAfterSwitch.length > 0) {
    return entriesAfterSwitch.sort((a,b) => a.entry.startTime - b.entry.startTime)[0];
  }

  return undefined;
}

const createEntriesWithSides = (sideA:IScheduleEntry[], sideB:IScheduleEntry[]):IEntryWithSide[] => {
  const entries:IEntryWithSide[] = [];

  for (const entry of sideA) {
    entries.push({side:"A", entry});
  }
  for (const entry of sideB) {
    entries.push({side:"B", entry});
  }

  return entries;
}

const findEntryAtTime = (entries:IEntryWithSide[], time:number) => {
  const matchingEntries = entries.filter(({entry}) => entry.startTime <= time && entry.endTime > time);
  if (matchingEntries.length === 0) {
    return undefined;
  }

  return matchingEntries.sort((a,b) => b.entry.endTime - a.entry.endTime)[0];
}

const mergeWithUniqueChangedIds = (originalEntries:IScheduleEntry[], changedEntries:IScheduleEntry[]) => {
  const usedIds = new Set<number>();
  let nextId = 1;

  for (const entry of originalEntries) {
    usedIds.add(entry.id);
    nextId = Math.max(nextId, entry.id + 1);
  }

  const result = originalEntries.map((entry) => Object.assign({}, entry));

  for (const changedEntry of changedEntries) {
    const entry = Object.assign({}, changedEntry);

    if (usedIds.has(entry.id)) {
      while (usedIds.has(nextId)) {
        nextId += 1;
      }
      entry.id = nextId;
    }

    usedIds.add(entry.id);
    nextId = Math.max(nextId, entry.id + 1);
    result.push(entry);
  }

  result.sort((a,b) => a.startTime - b.startTime);
  return mergeAndClean(result);
}

const setStartHandoverUnspecified = (entry:IScheduleEntry) => {
  entry.startAtSchool = false;
  entry.startLoc = UNSPECIFIED_LOCATION;
}

const setEndHandoverUnspecified = (entry:IScheduleEntry) => {
  entry.endAtSchool = false;
  entry.endLoc = UNSPECIFIED_LOCATION;
}

const getSwitchTime = (date:Date) => {
  const switchDate = new Date(date);
  switchDate.setHours(12,0,0,0);
  return switchDate.getTime();
}

const getStartOfDay = (date:Date) => {
  const dayStart = new Date(date);
  dayStart.setHours(0,0,0,0);
  return dayStart.getTime();
}

const getStartOfNextDay = (date:Date) => {
  const dayStart = new Date(date);
  dayStart.setHours(0,0,0,0);
  return addDays(dayStart, 1).getTime();
}


const shiftDays = (side:IScheduleEntry[], shift:number):IScheduleEntry[] => {


    for (const entry of side) {
        entry.startTime = addDays(new Date(entry.startTime), shift).getTime();
        entry.endTime = addDays(new Date(entry.endTime), shift).getTime();
    }

    side.sort((a,b) => a.startTime - b.startTime);

    return side;
}


export { applyChanges, shiftDays };



// WEBPACK FOOTER //
// ./src/components/calendar2/editPane/editActions/ShiftDays/ShiftDaysCalc.ts